import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Select — the shared themed dropdown used across the platform.
 *
 * Custom (non-native) so it matches the theme and feels premium. The options
 * panel is rendered in a PORTAL with fixed positioning, so it never gets
 * clipped by a modal or scroll container (a common problem with absolutely
 * positioned dropdowns).
 *
 *   <Select
 *     value={statusFilter}
 *     onChange={(v) => setStatusFilter(v)}
 *     options={[{ value: '', label: 'All Statuses' }, { value: 'active', label: 'Active' }]}
 *     leftIcon={<Filter className="w-4 h-4" />}
 *     size="sm"
 *     className="w-[170px]"
 *   />
 *
 * options: array of { value, label } (or plain strings)
 * onChange(value): called with the chosen option's value
 */

const normalize = (opts) =>
  (opts || []).map((o) => (o && typeof o === 'object' ? o : { value: o, label: String(o) }));

const Select = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  leftIcon,
  size = 'md',
  disabled = false,
  className = '',
  menuClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState(null); // { left, width, top? , bottom?, maxHeight }
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const opts = normalize(options);
  const selected = opts.find((o) => String(o.value) === String(value));

  const place = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < 240 && r.top > spaceBelow;
    setPos({
      left: r.left,
      width: r.width,
      top: openUp ? undefined : r.bottom + 6,
      bottom: openUp ? window.innerHeight - r.top + 6 : undefined,
      maxHeight: Math.min(280, (openUp ? r.top : spaceBelow) - 12),
    });
  };

  useLayoutEffect(() => {
    if (open) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    // Close on scroll/resize so the fixed panel never drifts from the trigger.
    const onReflow = () => setOpen(false);
    // Scroll is captured, so it also fires for the panel's own overflow-y —
    // scrolling a long option list is browsing, not drift, and must not close.
    const onScroll = (e) => {
      const t = e.target;
      if (t instanceof Node && panelRef.current && panelRef.current.contains(t)) return;
      onReflow();
    };
    document.addEventListener('mousedown', onDocDown);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // 'lg' matches the height of a FormField text input, so a select sitting
  // beside one in a grid row lines up exactly.
  const heightCls = size === 'sm'
    ? 'h-9 text-[13px]'
    : size === 'lg' ? 'h-[46px] text-[14px]' : 'h-10 text-[13.5px]';

  const choose = (val) => {
    if (onChange) onChange(val);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (active >= 0 && opts[active]) choose(opts[active].value);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive((i) => Math.min(opts.length - 1, i + 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    }
  };

  return (
    <div ref={triggerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        className={`w-full ${heightCls} inline-flex items-center gap-2 pl-3 pr-2.5 rounded-xl border font-medium text-left transition-colors
          ${open
            ? 'border-blue-500 ring-2 ring-blue-500/15 bg-white dark:bg-[#262b33]'
            : 'border-[#E3E9F3] dark:border-gray-700 bg-white dark:bg-[#262b33] hover:bg-[#F5F8FC] dark:hover:bg-[#31373f]'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        {leftIcon && <span className="text-[#94A3B8] shrink-0 flex items-center">{leftIcon}</span>}
        <span className={`flex-1 truncate ${selected ? 'text-[#334155] dark:text-gray-200' : 'text-[#94A3B8]'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-[#94A3B8] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="listbox"
          style={{
            position: 'fixed',
            left: pos.left,
            width: pos.width,
            top: pos.top,
            bottom: pos.bottom,
            maxHeight: pos.maxHeight,
          }}
          className={`z-[9999] rounded-xl border border-[#E3E9F3] dark:border-gray-700 bg-white dark:bg-[#262b33]
            shadow-[0_12px_32px_-8px_rgba(15,23,42,0.22)] p-1.5 overflow-y-auto ${menuClassName}`}
        >
          {opts.length === 0 ? (
            <div className="px-3 py-2 text-[13px] text-[#94A3B8]">No options</div>
          ) : (
            opts.map((o, i) => {
              const isSel = String(o.value) === String(value);
              return (
                <button
                  key={String(o.value)}
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  onClick={() => choose(o.value)}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] text-left transition-colors
                    ${isSel
                      ? 'bg-[#EEF3FF] dark:bg-blue-900/20 text-blue-600 font-semibold'
                      : active === i
                        ? 'bg-[#F5F8FC] dark:bg-[#31373f] text-[#334155] dark:text-gray-200'
                        : 'text-[#334155] dark:text-gray-300'}`}
                >
                  <span className="flex-1 truncate">{o.label}</span>
                  {isSel && <Check className="w-4 h-4 shrink-0" />}
                </button>
              );
            })
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default Select;
